import { useCallback, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { notification } from "antd";
import { useSlides } from "hooks/useSlide";
import {
  insertSlide,
  mergeSlideScriptIntoPrevious,
  parseScriptBySlides,
  removeSlideSegment,
} from "pages/media/projects/components/slides/utils";
import {
  buildDefaultSlide,
  type AddableSlideLayout,
} from "../helpers/buildDefaultSlide";
import { buildSlideInputs } from "../helpers/buildSlideInputs";
import { mapSlideToTemplateState } from "../helpers/mapTemplateSlidesToTemplateState";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "./useTemplateData";
import { useSaveSlidesAsset } from "./useSaveSlidesAsset";
import { useSaveTemplate } from "./useSaveTemplate";
import { useStoryboardEditing } from "./useStoryboardEditing";
import type { Slide } from "pages/media/types";
import type { MediaTemplate } from "types/";

const NARRATION_KEY =
  "components.templates.TemplatesBoard.storyboardSlidesThumbnails.defaultNarration";

const SLIDE_ID_REGEX = /^SLIDE-(\d+)$/;

const nextSlideId = (existing: Slide[]): string => {
  const max = existing.reduce((acc, slide) => {
    const match = SLIDE_ID_REGEX.exec(slide.id);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `SLIDE-${max + 1}`;
};

export const useSlideMutations = () => {
  const t = useTranslate();
  const {
    projectId,
    organizationId,
    storyboardWithSlidesData: { slides },
    storyboardSections,
    mediaGenerationSettingsData: { mediaAspectRatio, mediaGenerationSettings },
    brandKitData,
    templatesData: {
      presenter: initialPresenter,
      storyboard: storyboardMeta,
      storyboardId,
      primaryDocumentId,
      slidesPath,
    },
    logoRatio,
    presenterRatio,
    projectTheme,
  } = useTemplateData();

  const { insertSlideBefore, removeSlide: removeSlideFromCache } =
    useSlides(slidesPath);

  const { saveSlidesAsset } = useSaveSlidesAsset({
    organizationId: organizationId ?? undefined,
    projectId: projectId ?? undefined,
  });

  const { updateTemplate } = useSaveTemplate(organizationId ?? undefined);

  // No `markMediaOutOfDate`: every section update below follows a Slides
  // asset PATCH, which BE already marks out of date on its own.
  const { updateStoryboardSection } = useStoryboardEditing({
    projectId: projectId ?? "",
    organizationId: organizationId ?? "",
    storyboardId: storyboardId ?? undefined,
    primaryDocumentId: primaryDocumentId ?? undefined,
  });

  const {
    setTemplate,
    setTemplateState,
    deleteTemplate,
    setCurrentSlide,
    addSlideToTemplateMap,
    removeSlideFromTemplateMap,
    updateMainImage,
    serverTemplates,
  } = useTemplateEditorStore((state) => ({
    setTemplate: state.setTemplate,
    setTemplateState: state.setTemplateState,
    deleteTemplate: state.deleteTemplate,
    setCurrentSlide: state.setCurrentSlide,
    addSlideToTemplateMap: state.addSlideToTemplateMap,
    removeSlideFromTemplateMap: state.removeSlideFromTemplateMap,
    updateMainImage: state.updateMainImage,
    serverTemplates: state.serverTemplates,
  }));

  const [pendingSlideUuid, setPendingSlideUuid] = useState<string | null>(null);

  const slidesAssetId = storyboardMeta?.slides_asset_id;

  const buildTemplateForNewSlide = useCallback(
    (slide: Slide) => {
      if (!projectTheme || !mediaAspectRatio) return undefined;
      const themeLayout = projectTheme.layouts[slide.slide_type];
      if (!themeLayout) return undefined;

      return mapSlideToTemplateState(
        {
          id: "",
          sprite_definitions: themeLayout.layout,
          name: slide.uuid,
          avatarShot: null,
          music: null,
          inputs: {},
          speech: undefined,
        },
        [...slides, slide],
        mediaAspectRatio,
        {
          brandKitData: { logo: brandKitData.logo, audio: undefined },
          initialPresenter,
          presenterRatio,
          logoRatio,
          forceAdjustFont: true,
        }
      );
    },
    [
      projectTheme,
      mediaAspectRatio,
      slides,
      brandKitData.logo,
      initialPresenter,
      presenterRatio,
      logoRatio,
    ]
  );

  const performAdd = useCallback(
    async (params: {
      sectionId: string;
      arrayIndex: number;
      cacheBeforeUuid: string | null;
      scriptBeforeUuid: string | null;
      layout: AddableSlideLayout;
      mainImage?: {
        assetId: string;
        path: string;
        mediaType: "image" | "video";
      };
      /** Narration for the new slide; falls back to the default placeholder. */
      script?: string;
    }) => {
      if (!slidesAssetId) return;
      const {
        sectionId,
        arrayIndex,
        cacheBeforeUuid,
        scriptBeforeUuid,
        layout,
        mainImage,
        script,
      } = params;

      const chapterTemplateId = mediaGenerationSettings?.chapters.find(
        (c) => c.chapter_id === sectionId
      )?.chapter_template_id;
      if (!chapterTemplateId) return;

      const currentChapterTemplate = serverTemplates?.get(chapterTemplateId);
      if (!currentChapterTemplate) return;

      if (!projectTheme) return;

      const newUuid = crypto.randomUUID();
      const newId = nextSlideId(slides);
      const baseSlide = buildDefaultSlide(newUuid, newId, sectionId, layout, t);

      // Persist the picked main image's asset_id + media_type onto the new
      // slide's placeholders.image_element. This is the source of truth that
      // the editor reads back from the Slides asset on reload.
      const newSlide =
        mainImage && "image_element" in baseSlide.placeholders
          ? ({
              ...baseSlide,
              placeholders: {
                ...baseSlide.placeholders,
                image_element: {
                  ...baseSlide.placeholders.image_element,
                  asset_id: mainImage.assetId,
                  media_type: mainImage.mediaType,
                },
              },
            } as Slide)
          : baseSlide;

      const themeLayout = projectTheme.layouts[layout];
      if (!themeLayout) return;

      const newSlideDefinition = {
        name: newUuid,
        sprite_definitions: themeLayout.layout,
        inputs: buildSlideInputs(newUuid, layout),
      };

      const templateState = buildTemplateForNewSlide(newSlide);
      if (!templateState) return;

      const updatedChapterTemplate: MediaTemplate = {
        ...currentChapterTemplate,
        slides: [...(currentChapterTemplate.slides ?? []), newSlideDefinition],
      };

      const nextSlides = [
        ...slides.slice(0, arrayIndex),
        newSlide,
        ...slides.slice(arrayIndex),
      ];

      const section = storyboardSections.find((s) => s.id === sectionId);
      const currentScript = section?.script.content ?? "";
      const nextScript = insertSlide(
        currentScript,
        newUuid,
        scriptBeforeUuid,
        script ?? t(NARRATION_KEY)
      );

      // Slides cache first, so anything reading section.slides during the
      // store updates below already sees the new slide.
      insertSlideBefore(newSlide, cacheBeforeUuid);

      // Editor store: register the new slide's template, focus it, and link
      // its uuid → chapter template id so it shows up in thumbnails.
      setTemplate(newUuid, templateState);
      addSlideToTemplateMap(newUuid, chapterTemplateId);
      setTemplateState(templateState);
      setCurrentSlide(newUuid);

      // If the user picked a main image/video, apply it via the canonical
      // updateMainImage action — runs after setCurrentSlide so it targets
      // the new slide, and syncs templateState back into the templates map
      // through syncTemplateStateWithTemplates. Path is intentionally not
      // set: useRefreshAssetPath resolves it from assetId, which is the same
      // way the editor renders main images on a fresh page load. The picked
      // path from the gallery (esp. just-uploaded stock assets) is sometimes
      // not yet a working CDN URL, which would render as a broken image.
      if (mainImage && templateState.mainImage) {
        updateMainImage({
          assetId: mainImage.assetId,
          visible: true,
        });
      }

      setPendingSlideUuid(newUuid);
      try {
        // Order matters: chapter template first (so the SlideDefinition exists
        // server-side), then slides asset (so the slide_type lookup resolves),
        // then storyboard script. A partial commit leaves recoverable state.
        await updateTemplate(chapterTemplateId, updatedChapterTemplate);
        await saveSlidesAsset(slidesAssetId, nextSlides);
        await updateStoryboardSection(sectionId, {
          script: { content: nextScript },
        });
      } catch (error) {
        console.error("Failed to add slide", error);
        notification.error({ message: t("src.App.error") });
      } finally {
        setPendingSlideUuid(null);
      }
    },
    [
      slidesAssetId,
      slides,
      storyboardSections,
      mediaGenerationSettings,
      serverTemplates,
      projectTheme,
      saveSlidesAsset,
      updateTemplate,
      updateStoryboardSection,
      buildTemplateForNewSlide,
      setTemplate,
      addSlideToTemplateMap,
      setTemplateState,
      insertSlideBefore,
      setCurrentSlide,
      updateMainImage,
      t,
    ]
  );

  const addSlide = useCallback(
    async (
      anchorUuid: string,
      layout: AddableSlideLayout,
      position: "before" | "after",
      mainImage?: {
        assetId: string;
        path: string;
        mediaType: "image" | "video";
      },
      script?: string
    ) => {
      const anchorSlide = slides.find((s) => s.uuid === anchorUuid);
      const sectionId = anchorSlide?.section_id;
      if (!sectionId) return;

      const anchorIndex = slides.findIndex((s) => s.uuid === anchorUuid);

      if (position === "before") {
        return performAdd({
          sectionId,
          arrayIndex: anchorIndex,
          cacheBeforeUuid: anchorUuid,
          scriptBeforeUuid: anchorUuid,
          layout,
          mainImage,
          script,
        });
      }

      // "after": script stays scoped to the section — if the anchor is the
      // last slide of its section, append at end (null); otherwise insert
      // before the opening tag of the next slide in the same section.
      const section = storyboardSections.find((s) => s.id === sectionId);
      const sectionSlides = section?.slides ?? [];
      const indexInSection = sectionSlides.findIndex(
        (s) => s.uuid === anchorUuid
      );
      const scriptBeforeUuid =
        indexInSection >= 0
          ? (sectionSlides[indexInSection + 1]?.uuid ?? null)
          : null;

      return performAdd({
        sectionId,
        arrayIndex: anchorIndex + 1,
        cacheBeforeUuid: slides[anchorIndex + 1]?.uuid ?? null,
        scriptBeforeUuid,
        layout,
        mainImage,
        script,
      });
    },
    [slides, storyboardSections, performAdd]
  );

  const removeSlide = useCallback(
    async (slideUuid: string, options?: { discardScript?: boolean }) => {
      if (!slidesAssetId) return;

      const target = slides.find((s) => s.uuid === slideUuid);
      const sectionId = target?.section_id;
      if (!sectionId) return;

      const chapterTemplateId = mediaGenerationSettings?.chapters.find(
        (c) => c.chapter_id === sectionId
      )?.chapter_template_id;

      const currentChapterTemplate = chapterTemplateId
        ? serverTemplates?.get(chapterTemplateId)
        : undefined;
      const updatedChapterTemplate: MediaTemplate | undefined =
        currentChapterTemplate
          ? {
              ...currentChapterTemplate,
              slides: (currentChapterTemplate.slides ?? []).filter(
                (s) => s.name !== slideUuid
              ),
            }
          : undefined;

      const nextSlides = slides.filter((s) => s.uuid !== slideUuid);
      const section = storyboardSections.find((s) => s.id === sectionId);
      const currentScript = section?.script.content ?? "";
      // Keep the deleted scene's narration by default; drop it if requested.
      const nextScript = options?.discardScript
        ? removeSlideSegment(currentScript, slideUuid)
        : mergeSlideScriptIntoPrevious(currentScript, slideUuid);

      // Prefer the previous scene in script order (the one that now holds the
      // merged narration); fall back to an adjacent slide.
      const scriptOrder = [...parseScriptBySlides(currentScript).keys()];
      const segIndex = scriptOrder.indexOf(slideUuid);
      const previousSceneUuid =
        segIndex > 0 ? scriptOrder[segIndex - 1] : undefined;
      const removedIndex = slides.findIndex((s) => s.uuid === slideUuid);
      const neighbour =
        previousSceneUuid ??
        slides[removedIndex - 1]?.uuid ??
        slides[removedIndex + 1]?.uuid;

      removeSlideFromCache(slideUuid);
      deleteTemplate(slideUuid);
      removeSlideFromTemplateMap(slideUuid);
      if (neighbour) setCurrentSlide(neighbour);

      try {
        if (chapterTemplateId && updatedChapterTemplate) {
          await updateTemplate(chapterTemplateId, updatedChapterTemplate);
        }
        await saveSlidesAsset(slidesAssetId, nextSlides);
        await updateStoryboardSection(sectionId, {
          script: { content: nextScript },
        });
      } catch (error) {
        console.error("Failed to remove slide", error);
        notification.error({ message: t("src.App.error") });
      }
    },
    [
      slidesAssetId,
      slides,
      storyboardSections,
      mediaGenerationSettings,
      serverTemplates,
      saveSlidesAsset,
      updateTemplate,
      updateStoryboardSection,
      removeSlideFromCache,
      deleteTemplate,
      removeSlideFromTemplateMap,
      setCurrentSlide,
      t,
    ]
  );

  return { addSlide, removeSlide, pendingSlideUuid };
};
